import type { QueryObserverResult } from '@tanstack/query-core'
import { useQuery } from '@tanstack/react-query'

import { query_get_users_by_wallet } from '@repo/common/queries/admin/user/query_get_users_by_wallet.ts'
import { graphql_call as graphql_admin_call } from '@repo/common/queries/schemas/server_admin_query'

export interface AdminUserData {
  id: string
  uuid: string
  email: string | null
  name: string | null
  wallet_address: string | null
  wallet_provider: string | null
  roles: string[]
  active_at: string | null
  created_at: string
  updated_at: string
  image: string | null
  locked: boolean
}

export interface AdminUsersByWalletParams {
  walletAddresses: string[]
}

export interface UsersByWalletResult {
  data: AdminUserData[] | null
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<QueryObserverResult<AdminUserData[]>>
}

/**
 * Fetch admin user details by their wallet addresses.
 * @param params list of addresses to query
 * @returns user details in array parallel to input addresses
 */
export function useAdminUsersByWallet(
  params: AdminUsersByWalletParams,
): UsersByWalletResult {
  const { walletAddresses } = params

  const queryResult = useQuery({
    queryKey: ['adminUsersByWallet', walletAddresses],
    queryFn: async () => {
      if (walletAddresses.length === 0) {
        return []
      }

      const result = await graphql_admin_call({
        query: query_get_users_by_wallet,
        variables: {
          wallet_addresses: walletAddresses,
        },
      })

      return result as AdminUserData[]
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: walletAddresses.length > 0,
  })

  return {
    data: queryResult.data || null,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}
